<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Aktive Trainergeschichte
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Betreutes Wohnen</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :classificationDropdown="classificationDropdown"
            :officeDropdown="officeDropdown"
            :unemployedDropdown="unemployedDropdown"
            :clientDropdown="clientDropdown"
            @search="getCoachingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCoachingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>

            <ExcelExportButton
                :label="'Excelexport Jahresauswertung'"
                :loading="isExcelLoaded2"
                @click="getExcelExport2"/>
          </div>
        </div>

        <div class="row mb-2 mt-4">
          <div class="col-12">
            <p style="font-size: 16px; margin-bottom: 5px;">Anzahl der Coachings: {{ coachingList?.pagination?.total_items }}</p>
          </div>
        </div>


        <div class="table-responsive overflow-auto pt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Status</th>
              <th>Fertig um</th>
              <th>Kunden</th>
              <th>Standort</th>
              <th>JC / AA Nummer</th>
              <th>Kostenträger</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Stand</th>
              <th>Stand %</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in coachingList.data" :key="coaching.id">
              <td>{{ coaching?.statusTranslation }}</td>
              <td>{{ coaching?.finishedAt }}</td>
              <td>{{ coaching?.unemployed?.firstName }} {{ coaching?.unemployed?.lastName }}</td>
              <td>{{ coaching?.unemployed?.location?.name }}</td>
              <td>{{ coaching?.unemployed?.aaNumber }}</td>
              <td>{{ coaching?.client?.name }}</td>
              <td>{{ coaching.startDate }}</td>
              <td>{{ coaching.endDate }}</td>
              <td>{{ coaching.unitsDone }}</td>
              <td>{{ coaching.unitsDonePercent }} %</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="coachingList?.pagination?.total_pages > 1"
            :totalPages="coachingList?.pagination?.total_pages"
            :currentPage="coachingList?.pagination?.current_page"
            :totalItems="coachingList?.pagination?.total_items"
            :perPage="coachingList?.pagination?.items_per_page"
            @pagechanged="getCoachingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsService from '@/api/Analytics';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import {
  assistedLivingCoachingClassificationDropdown,
  getAllUnemployedDropdown,
  getClientDropdown,
  getOfficeDropdown
} from '@/shared/dropdowns';
import { downloadFile } from '@/shared/functions';
import Search from '@/views/App/Analytics/ActiveCoachingHistory/Search.vue';
import { onMounted, ref } from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const currentDate = new Date().getFullYear();
const classificationDropdown = assistedLivingCoachingClassificationDropdown();

const filters = ref({
  officeIds: [],
  clientId: '',
  unemployedId: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
});

const coachingList       = ref([]);
const officeDropdown     = ref([]);
const clientDropdown     = ref([]);
const unemployedDropdown = ref([]);
const isSearching        = ref(false);
const isLoaded           = ref(false);
const isExcelLoaded      = ref(false);
const isExcelLoaded2     = ref(false);

async function getCoachingList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachingList.value?.pagination?.items_per_page;

  try {
    const { data } = await analyticsService.getActiveCoachingHistoryAssistedLivingCoaching({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachingList.value = data;
    isSearching.value  = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getExcelExport() {
  await downloadFile('/analytics/active-coaching-history/assisted-living-coaching/excel-export',
      undefined,
      {
        ...filters.value,
      },
      false,
      'GET',
      isExcelLoaded
  )
}

async function getExcelExport2() {
  await downloadFile('/active-coaching-history/assisted-living-coaching-first-interview/excel-export',
      undefined,
      {
        ...filters.value,
      },
      false,
      'GET',
      isExcelLoaded2
  )
}

onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getAllUnemployedDropdown(unemployedDropdown),
    getClientDropdown(clientDropdown),
    getCoachingList()
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>